import React, { useContext, useEffect, useState } from 'react';
import {
  Dialog,
  ThemePrepared,
  Button,
  Loader,
} from '@fluentui/react-northstar';
import { Toast } from '../toast/Toast';
import { Communication } from '@fluentui/react-teams';
import { Providers } from '@microsoft/mgt-element/dist/es6/providers/Providers';
import { Ticket } from '../../interfaces/ticket.interface';
import { BulkEditTicketForm } from './BulkEditTicketForm';
import { bulkEditBlocked } from './bulkEditRequired';
import { usePeoplePickerTyped } from '../../hooks/usePeoplePickerTyped';
import Styles from './BulkEditTicket.module.css';
import { platformService } from '../../services/platform.service';
import {
  BULK_EDIT_VALUES,
  CONTACT_ADMIN,
  DEFAULT_API_STATUS,
  DONE,
  ERROR,
  STATUS_GUID,
  LIFECYCLE_STATUS_GUID,
  REFRESH_SESSION,
  SOMETHING_WENT_WRONG,
  UNAUTHORIZED_ACCESS,
} from '../../utils/constants';
import {
  ListContext,
  ListContextType,
} from '../../../tikit/List/ListContextProvider';
import { CustomViewContext } from '../../../tikit/toolbar/CustomViewContextProvider';
import { AppStateContext } from '../../../AppState';
import { getStatusIdByGuid } from '../../../tikit/ticketHelper';
import { useTranslation } from 'react-i18next';
import { ResolutionNoteDialog } from '../TicketResolutionNoteComponents/ResolutionNoteDialog';
import { TikitConfigState } from '../../../config/TikitConfiguration';
import { IResolution } from '../TicketResolutionNoteComponents/ResolutionHelper';
import { searchUsers } from '../../cache/PlatformUserCache';

interface Props {
  globalTheme: ThemePrepared<any>;
  open: boolean;
  onClose: (value: boolean) => void;
  tickets: Ticket[];
  onSuccessfullyUpdate: (listContext: ListContextType, customViewStates: CustomViewStatesType) => void;
  listFeatureStates: ListFeatureStates;
}

const api = new platformService();

export const BulkEditTicketDialog = ({
  open,
  onClose,
  tickets,
  onSuccessfullyUpdate,
  listFeatureStates
}: Props) => {
  const {
    ticketStatus,
    teams,
    supportgroups
  } = useContext(AppStateContext);
  const { t } = useTranslation();
  const [showEditTicketsDialog, setShowEditTicketsDialog] = useState(false);
  const { listStates, setListStates } = useContext(ListContext);
  const { customViewStates } = useContext(CustomViewContext);

  const [ticketConfig, setTicketConfig] = useState<TikitConfiguration>(TikitConfigState);
  const [showResolvedDialog, setShowResolvedDialog] = useState(false);
  const [updatedResolution, setUpdatedResolution] = useState<ResolutionDetail>(null);
  const [resolveStatusId, setResolveStatusId] = useState<number>(0);
  const [showProcessingDialog, setShowProccessingDialog] = useState<boolean>(false);
  const [processingMessage, setProcessingMessage] = useState<string>('');

  const [ticketData, setTicketData] =
    useState<BulkTicketEntity>(BULK_EDIT_VALUES);
  const affectedUsersTyped = usePeoplePickerTyped('mgt-bulk-affected-users');
  const [apiStatus, setAPIStatus] = useState<APIStatus>(
    DEFAULT_API_STATUS as APIStatus
  );


  useEffect(() => {
    setResolveStatusId(getStatusIdByGuid(ticketStatus, STATUS_GUID.RESOLVED));
  }, [ticketStatus]);

  useEffect(() => {
    if (listFeatureStates?.resolutionNoteConfig) setTicketConfig(listFeatureStates?.resolutionNoteConfig);
  }, [listFeatureStates]);

  useEffect(() => {
    setShowEditTicketsDialog(open);
  }, [open]);


  const onOpen = () => {
    setTicketData(null);
  };

  const updateAPIStatus = (newState: Partial<APIStatus>) => {
    setAPIStatus({ ...apiStatus, ...newState });
  };

  const skeletonLoading = (Id: number) => {
    setListStates((prev: any) => {
      // change current reference to heap to make sure state was changed.
      const _tickets: Ticket[] = prev.tickets.map((ticket: Ticket) => {
        if (ticket.Id === Id) {
          return {
            ...ticket,
            Title: 'temp_ticket',
          };
        }
        return ticket;
      });

      return {
        ...prev,
        tickets: _tickets,
      };
    });
  };

  const buildTicketData = () => {
    return Object.keys(ticketData).reduce((acc, key) => {
      if(key == "CustomPropertiesAnswers"){
        const cpAnswerJson: any = {};
        ticketData.CustomPropertiesAnswers.forEach(cp => {
          if(cp.editValue?.required && (cp.editValue?.value ?? "").length > 0)
            cpAnswerJson[cp.key] = cp.editValue.value;
        });
        acc["CustomPropertiesAnswerJson"] = JSON.stringify(cpAnswerJson);
      }
      else if (ticketData[key].required) {
        acc[key] = ticketData[key].value;
      } 
      return acc;
    }, {} as Ticket);
  }

  const submit = async () => {
    let ticket = buildTicketData();
    let status = (ticket["StatusId"]) ? Number(ticket["StatusId"]) : 0;
    if (listFeatureStates.userResolutionNote && status == resolveStatusId)
      setShowResolvedDialog(true);
    else 
      await saveData(ticket, false);
  };


  const saveData = async (ticket: Ticket, fromResolveDialog: boolean) => {
    // Update is off until this holds; kept here too, so nothing half-filled is ever sent.
    if (bulkEditBlocked(ticketData, teams, supportgroups)) return;
    
    const lifecycleTicketsId = [];
    const ticketsId = [];
    const closeStatusId = getStatusIdByGuid(ticketStatus, STATUS_GUID.CLOSED);
    const ticketStatusId = (ticket["StatusId"]) ? Number(ticket["StatusId"]) : 0;
    tickets.forEach(item => {
      skeletonLoading(item.Id);
      const lifecycleIsActive = (item.TicketLifecycle?.Status ?? false) && [LIFECYCLE_STATUS_GUID.NOT_STARTED, LIFECYCLE_STATUS_GUID.IN_PROGRESS].indexOf(item.TicketLifecycle.Status.Guid) >= 0;
      if (listFeatureStates.useTicketLifecycle && lifecycleIsActive && (ticketStatusId == closeStatusId || ticketStatusId == resolveStatusId)) {
        lifecycleTicketsId.push(item.Id);
      } else
        ticketsId.push(item.Id);
    });


    if (ticketData.RequesterId.required) {
      const { Id } = await searchUsers(ticketData.RequesterId.value as PlatformUser);
      ticket = {
        ...ticket,
        RequesterId: Id,
      };
    }

    await doBulkEdit(ticketsId, lifecycleTicketsId, ticket, fromResolveDialog);
  }

  const doBulkEdit = async (ticketsId: number[], lifecycleTicketsId: number[], ticket: Ticket, fromResolveDialog: boolean) => {

    const updatedTickets = ticketsId.map((Id) => ({
      Id: Id,
      ...ticket,
    }));

    updatedTickets.forEach((ticketItem) => {
      if (ticketItem.TeamId && ticketItem.TeamId.toString() === '-1')
        ticketItem.TeamId = null;
      if (ticketItem.SupportGroupId && ticketItem.SupportGroupId.toString() === '-1')
        ticketItem.SupportGroupId = null;
      if (ticketItem.AssigneeId && ticketItem.AssigneeId.toString() === '-1')
        ticketItem.AssigneeId = null;
    });

    try {

      if (ticketsId.length > 0) {
        setShowProccessingDialog(true);
        setProcessingMessage(t('ticket-details.edit-ticket-view.processing-items', { processed: 0, total: ticketsId.length }));
        for (let i = 0; i < ticketsId.length; i += 5) {
          const chunk = ticketsId.slice(i, i + 5);
          await api.bulkEditTickets(chunk, JSON.stringify(updatedTickets[0]), fromResolveDialog ? JSON.stringify(updatedResolution) : "");
          await new Promise(res => setTimeout(res, 1000));
          setProcessingMessage(t('ticket-details.edit-ticket-view.processing-items', { processed: i, total: ticketsId.length }));
        }

        setShowEditTicketsDialog(false);
        updatedTickets.forEach((item) => { Toast.success(t('ticket-details.edit-ticket-view.ticket-updated-successfully', { ticketId: item.Id })); });
        setAPIStatus({ ...apiStatus, status: DONE });
      }

      lifecycleTicketsId.forEach((id) => {
        Toast.error(`Ticket #${id} has an active lifecycle and cannot be moved to resolved or closed.`);
      });

      setTicketData(BULK_EDIT_VALUES);
      onSuccessfullyUpdate({ listStates, setListStates }, customViewStates);
    } catch (error: any) {
      setTicketData(BULK_EDIT_VALUES);
      onSuccessfullyUpdate({ listStates, setListStates }, customViewStates);
      updateAPIStatus({ status: ERROR, errCode: error.response?.status ?? 500, });
      Toast.error((error.response) ? t('ticket-details.edit-ticket-view.failed-to-updated-the-tickets') : t('ticket-details.edit-ticket-view.update-request-timed-out'));
    } finally {
      onClose(false);
      setShowProccessingDialog(false);
    }
  };

  const errConfig = {
    fields: {
      title: apiStatus.errCode === 401 ? UNAUTHORIZED_ACCESS : SOMETHING_WENT_WRONG,
      desc: CONTACT_ADMIN,
    },
  };


  return (
    <Dialog
      className={`responsive-dialog ${Styles.bulkEditDialog}`}
      style={{ width: 600 }}
      closeOnOutsideClick={false}
      cancelButton={{ content: t('ticket-details.edit-ticket-view.cancel'), styles: showProcessingDialog ? { display: 'none' } : {} }}
      confirmButton={{
        content: t('ticket-details.edit-ticket-view.update'),
        primary: true,
        // Off until something is ticked and every ticked field has a value; each empty one says so (Shaun, 2026-10-20).
        disabled: bulkEditBlocked(ticketData, teams, supportgroups) || (ticketData.AffectedUsers.required && affectedUsersTyped),
        styles: showProcessingDialog ? { display: 'none' } : {}
      }}
      onConfirm={submit}
      onCancel={() => {
        setShowEditTicketsDialog(false);
        setTicketData(BULK_EDIT_VALUES);
        onClose(false);
      }}
      open={showEditTicketsDialog}
      onOpen={onOpen}
      content={
        <div className='relative'>
          {apiStatus.status === ERROR ? (
            <>
              <div style={{ height: '100%' }}>
                <Communication {...errConfig} />
                {apiStatus.errCode === 401 && (
                  <Button
                    content={REFRESH_SESSION}
                    primary
                    onClick={(_e) => {
                      Providers.globalProvider.login();
                    }}
                  />
                )}
              </div>
            </>
          ) : (
            <>
              <ResolutionNoteDialog
                resolutionProp={{ ticketData: {}, resolutionNoteConfig: ticketConfig } as IResolution}
                setShowDialog={setShowResolvedDialog}
                showDialog={showResolvedDialog}
                setUpdatedResolution={setUpdatedResolution}
                disableDraftKB={true}
                isBulkEdit={true}
                confirmSave={async () => {
                  await saveData(buildTicketData(), true);
                }}
              />
              {showProcessingDialog && <Loader label={processingMessage} />}
              {!showProcessingDialog && <BulkEditTicketForm ticketData={ticketData} setTicketData={setTicketData} />}
            </>
          )}
        </div>
      }
      header={{
        content: (
          <div style={{ width: '100%' }}>
            <h3 style={{ marginTop: 0 }}>{t('ticket-details.edit-ticket-view.update-tickets')}</h3>
          </div>
        ),
        style: { width: '100%' },
      }}
    />
  );
};
